"use client";

import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { home } from "@faire/retailer-visitor-shared/paths";
import { useSettingLoggedInHomePageRouteChangePrefetching } from "@faire/retailer-visitor-shared/settings/useSettingLoggedInHomePageRouteChangePrefetching";
import { useAssignSettingLoggedInHomePageRouteChangePrefetching } from "@faire/retailer-visitor-shared/settings/useSettingLoggedInHomePageRouteChangePrefetching";
import { useSettingIsPerfAppSemDiscoverTreatment } from "@faire/retailer-visitor-shared/settings/useSettingPerfAppSemDiscover";
import { FaireLogo } from "@faire/web--source/slate/logos/FaireLogo";
import { getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types";
import React, { useMemo } from "react";
import styled, { css } from "styled-components";

import { HeaderLink } from "../styled";
import { useHeaderState } from "../useHeaderState";

type Props = {
  isLoggedInRetailer: boolean;
};

export const HeaderLogo: React.FC<Props> = ({ isLoggedInRetailer }) => {
  const { isShowingMenu } = useHeaderState();
  const { prefetch: nextPrefetch } = useNextRouterContext();

  const isPerfAppSemDiscoverPageTreatment =
    useSettingIsPerfAppSemDiscoverTreatment();
  const isLoggedInHomePageRouteChangePrefetching =
    useSettingLoggedInHomePageRouteChangePrefetching();
  const assignSettingLoggedInHomePageRouteChangePrefetching =
    useAssignSettingLoggedInHomePageRouteChangePrefetching();

  const onLogoHover = useMemo(() => {
    return () => {
      nextPrefetch(home(), {
        kind: isLoggedInHomePageRouteChangePrefetching
          ? PrefetchKind.FULL
          : PrefetchKind.AUTO,
      });
    };
  }, [nextPrefetch, isLoggedInHomePageRouteChangePrefetching]);

  const onLogoClick = useMemo(() => {
    return () => assignSettingLoggedInHomePageRouteChangePrefetching();
  }, [assignSettingLoggedInHomePageRouteChangePrefetching]);

  return (
    <HeaderLogoLink
      id="faire-logo-link"
      to={home()}
      $marginTop={isLoggedInRetailer}
      onMouseEnter={onLogoHover}
      onTouchStart={onLogoHover}
      onClick={onLogoClick}
      $absolutePositioning={
        !isLoggedInRetailer && !isPerfAppSemDiscoverPageTreatment
      }
    >
      <RepsonsiveFaireLogo
        mobileSmall
        inverse={isShowingMenu}
        className={isPerfAppSemDiscoverPageTreatment ? "pl-6" : ""}
      />
    </HeaderLogoLink>
  );
};

const HeaderLogoLink = styled(HeaderLink)<{
  $marginTop: boolean;
  $absolutePositioning: boolean;
}>`
  padding-left: 0;
  ${(props) =>
    props.$absolutePositioning
      ? css`
          top: 0;
          position: absolute;
          @media (min-width: 0px) and (max-width: 320px) {
            left: 44px;
          }
          @media (min-width: 321px) and (max-width: 390px) {
            left: 48px;
          }
          @media (min-width: 391px) {
            left: 54px;
          }
        `
      : ""};
  padding-right: 0;
  transition: left 0.1s linear;
  ${responsiveMediaQueries.mobile} {
    ${(props) =>
      props.$marginTop
        ? css`
            margin-top: ${getSpacing("2x")};
          `
        : null}
  }
`;

const RepsonsiveFaireLogo = styled(FaireLogo)`
  ${responsiveMediaQueries.tabletAndBelow} {
    width: 104px;
  }
`;
